import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getItemWithExpiration, isCenterViewTab } from '@/lib/utils';
import { groupTicketKeys } from '@/features/groupTicket/keys';
import { groupSessionKeys } from '@/features/groupSession/keys';
import { membershipChangeLogKeys } from '@/features/membershipChangeLog/keys';
import { cancelPause, changePauseEnd, openPause, probePauseSupport, resumePause } from './api';
import { membershipPauseKeys } from './keys';
import { isLegacyServerNotFound } from './errors';
import { ChangePauseEndBody, OpenPauseBody, PausePassRef } from './types';

const getAuthCenterId = () => String(getItemWithExpiration('authObject')?.centerId || '');

/**
 * 정지 API 가 있는 서버인가. 세션 동안 한 번만 묻는다(`api.ts` probePauseSupport).
 * - 센터 열람 탭은 쓰기 요청 자체가 막히므로(apiClient 가 알림을 띄움) 묻지 않고 미지원으로 본다.
 * - 판단 불가(네트워크·5xx)는 미지원 — 메뉴를 숨기는 쪽으로 닫는다. 실패 결과는 캐시하지 않아 다음 화면에서 다시 묻는다.
 */
export const useMembershipPauseSupport = (centerIdArg?: string | null) => {
    const centerId = String(centerIdArg || getAuthCenterId());
    const viewOnly = isCenterViewTab();
    const query = useQuery({
        queryKey: membershipPauseKeys.support(centerId),
        queryFn: () => probePauseSupport(centerId),
        enabled: !!centerId && !viewOnly,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
    return {
        isSupported: !viewOnly && query.data === true,
        isChecking: !viewOnly && !!centerId && query.isLoading,
    };
};

/** 실제 정지 호출이 구서버 404 를 받으면 지원 판정을 내린다(롤백 배포 등). */
export const markPauseUnsupportedIfLegacy = (queryClient: QueryClient, error: unknown, centerIdArg?: string | null) => {
    if (!isLegacyServerNotFound(error)) return false;
    const centerId = String(centerIdArg || getAuthCenterId());
    if (centerId) queryClient.setQueryData(membershipPauseKeys.support(centerId), false);
    return true;
};

/** 정지 쓰기 뒤 다시 불러올 화면들 — 상세·카드 목록·생성용 회원 목록·공유 이용권·그룹 목록·변경 이력 */
export const invalidateAfterPauseWrite = (queryClient: QueryClient) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
        queryClient.invalidateQueries({ queryKey: ['getMemberListForCenterAPI'] }),
        queryClient.invalidateQueries({ queryKey: ['sharedMembership', 'staffEntitlements'] }),
        queryClient.invalidateQueries({ queryKey: groupTicketKeys.all }),
        queryClient.invalidateQueries({ queryKey: groupSessionKeys.all }),
        queryClient.invalidateQueries({ queryKey: membershipChangeLogKeys.all }),
    ]);

const useAfterWrite = () => {
    const queryClient = useQueryClient();
    return {
        onSuccess: () => {
            invalidateAfterPauseWrite(queryClient);
        },
        onError: (error: unknown) => {
            markPauseUnsupportedIfLegacy(queryClient, error);
        },
    };
};

export const useOpenPause = () =>
    useMutation({
        mutationFn: ({ ref, body }: { ref: PausePassRef; body: OpenPauseBody }) => openPause(ref, body),
        ...useAfterWrite(),
    });

export const useResumePause = () =>
    useMutation({
        mutationFn: ({ ref, pauseId, requestKey }: { ref: PausePassRef; pauseId: string; requestKey: string }) =>
            resumePause(ref, pauseId, requestKey),
        ...useAfterWrite(),
    });

export const useCancelPause = () =>
    useMutation({
        mutationFn: ({ ref, pauseId, requestKey }: { ref: PausePassRef; pauseId: string; requestKey: string }) =>
            cancelPause(ref, pauseId, requestKey),
        ...useAfterWrite(),
    });

export const useChangePauseEnd = () =>
    useMutation({
        mutationFn: ({ ref, pauseId, body }: { ref: PausePassRef; pauseId: string; body: ChangePauseEndBody }) =>
            changePauseEnd(ref, pauseId, body),
        ...useAfterWrite(),
    });
